import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

const Grievances = () => {
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState({});
  
  // Filters and search
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    department: 'all',
    search: '',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });
  
  // Modal state
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [responseForm, setResponseForm] = useState({
    status: '',
    priority: '',
    adminResponse: '',
    category: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchGrievances();
  }, [filters]);

  const fetchGrievances = async () => {
    try {
      setLoading(true);
      const response = await apiService.getGrievances(filters);
      if (response.success) {
        setGrievances(response.data.grievances);
        setStatistics(response.data.statistics);
      }
    } catch (err) {
      console.error('Error fetching grievances:', err);
      setError('Failed to load grievances');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchGrievances();
  };

  const openGrievanceModal = (grievance) => {
    setSelectedGrievance(grievance);
    setResponseForm({
      status: grievance.status,
      priority: grievance.priority,
      adminResponse: grievance.adminResponse || '',
      category: grievance.category
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedGrievance(null);
    setResponseForm({
      status: '',
      priority: '',
      adminResponse: '',
      category: ''
    });
  };

  const handleResponseSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGrievance) return;

    setSubmitting(true);
    try {
      const response = await apiService.updateGrievanceStatus(selectedGrievance.id, responseForm);
      if (response.success) {
        await fetchGrievances();
        closeModal();
        alert('Grievance updated successfully!');
      }
    } catch (err) {
      console.error('Error updating grievance:', err);
      alert('Failed to update grievance: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this grievance? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.deleteGrievance(id);
      if (response.success) {
        await fetchGrievances();
        alert('Grievance deleted successfully!');
      }
    } catch (err) {
      console.error('Error deleting grievance:', err);
      alert('Failed to delete grievance: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_review: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Grievance Management</h1>
          <p className="text-gray-600">Manage and respond to student grievances</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <i className="fas fa-clock text-yellow-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.pending || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <i className="fas fa-eye text-blue-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">In Review</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.inReview || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <i className="fas fa-check-circle text-green-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.resolved || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <i className="fas fa-exclamation-triangle text-red-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Urgent</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.urgent || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_review">In Review</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <select
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Departments</option>
              <option value="Civil Engineering">Civil Engineering</option>
              <option value="Computer Science & Engineering">CSE</option>
              <option value="Electronics & Communication Engineering">ECE</option>
              <option value="Mechanical Engineering">Mechanical</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <form onSubmit={handleSearch} className="flex">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by name, ID, email..."
                className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <i className="fas fa-search"></i>
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Grievances Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {grievances.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <i className="fas fa-inbox text-4xl mb-4"></i>
            <p>No grievances found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {grievances.map((grievance) => (
                  <tr key={grievance.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {grievance.isAnonymous ? 'Anonymous' : grievance.fullName}
                        </div>
                        <div className="text-sm text-gray-500">{grievance.studentId}</div>
                        <div className="text-sm text-gray-500">{grievance.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {grievance.department || 'Not specified'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {grievance.category || 'Other'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(grievance.status)}`}>
                        {grievance.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(grievance.priority)}`}>
                        {grievance.priority.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(grievance.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openGrievanceModal(grievance)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        View/Respond
                      </button>
                      <button
                        onClick={() => handleDelete(grievance.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for viewing/responding to grievance */}
      {showModal && selectedGrievance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Grievance Details - ID: {selectedGrievance.id}</h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              {/* Grievance Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Student Information</h3>
                  <div className="space-y-2">
                    <p><strong>Name:</strong> {selectedGrievance.isAnonymous ? 'Anonymous' : selectedGrievance.fullName}</p>
                    <p><strong>Student ID:</strong> {selectedGrievance.studentId}</p>
                    <p><strong>Email:</strong> {selectedGrievance.email}</p>
                    <p><strong>Phone:</strong> {selectedGrievance.phoneNumber || 'Not provided'}</p>
                    <p><strong>Department:</strong> {selectedGrievance.department || 'Not specified'}</p>
                    <p><strong>Anonymous:</strong> {selectedGrievance.isAnonymous ? 'Yes' : 'No'}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-4">Grievance Status</h3>
                  <div className="space-y-2">
                    <p><strong>Status:</strong> 
                      <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedGrievance.status)}`}>
                        {selectedGrievance.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </p>
                    <p><strong>Priority:</strong> 
                      <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(selectedGrievance.priority)}`}>
                        {selectedGrievance.priority.toUpperCase()}
                      </span>
                    </p>
                    <p><strong>Category:</strong> {selectedGrievance.category || 'Other'}</p>
                    <p><strong>Submitted:</strong> {new Date(selectedGrievance.createdAt).toLocaleString()}</p>
                    {selectedGrievance.respondedBy && (
                      <p><strong>Responded by:</strong> {selectedGrievance.respondedBy}</p>
                    )}
                    {selectedGrievance.resolvedAt && (
                      <p><strong>Resolved:</strong> {new Date(selectedGrievance.resolvedAt).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Grievance Description */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Grievance Description</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{selectedGrievance.grievanceDescription}</p>
                </div>
              </div>

              {/* Admin Response Form */}
              <form onSubmit={handleResponseSubmit}>
                <h3 className="text-lg font-semibold mb-4">Admin Response</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={responseForm.status}
                      onChange={(e) => setResponseForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="pending">Pending</option>
                      <option value="in_review">In Review</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={responseForm.priority}
                      onChange={(e) => setResponseForm(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={responseForm.category}
                      onChange={(e) => setResponseForm(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="Academic">Academic</option>
                      <option value="Administrative">Administrative</option>
                      <option value="Infrastructure">Infrastructure</option>
                      <option value="Faculty">Faculty</option>
                      <option value="Hostel">Hostel</option>
                      <option value="Harassment">Harassment</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Response Message</label>
                  <textarea
                    value={responseForm.adminResponse}
                    onChange={(e) => setResponseForm(prev => ({ ...prev, adminResponse: e.target.value }))}
                    rows="6"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Enter your response to the student..."
                  />
                </div>

                {selectedGrievance.adminResponse && (
                  <div className="mb-4">
                    <h4 className="text-md font-semibold mb-2">Previous Response</h4>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="whitespace-pre-wrap">{selectedGrievance.adminResponse}</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Updating...
                      </>
                    ) : (
                      'Update & Respond'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Grievances;